import {
  BiqlQuery,
  BiqlField,
  CalculatedField,
  QubesAPI,
} from "../../qubes-client/api";
import { ColumnDef } from "./types";
import { DefaultTableModel } from "./TableModel";
import { StringDataType, NumberDataType } from "./DataType";
import { ReactNode } from "react";
import { RowAction } from "./RowActions";
import { RowAction as OldRowAction } from "../qubes-table/table-model";
import { notify } from "../notifications/NotificationProvider";

/**
 * Creates a new table model from a BiqlQuery
 * This adapter bridges BiqlQuery (used with MUI DataGrid) to the new table-model widget
 */
export class BiqlTableModel<TRow extends Record<string, unknown> = Record<string, unknown>> extends DefaultTableModel<TRow> {
  private biqlQuery: BiqlQuery;
  private realtimeUpdateCallback?: (rows: TRow[]) => void;
  public rowActions?: RowAction<TRow>[];

  constructor(
    biqlQuery: BiqlQuery,
    oldRowActions?: OldRowAction<TRow>[],
    onRealtimeUpdate?: (rows: TRow[]) => void,
  ) {
    // Start with empty data - will be populated by query
    const columns =
      BiqlTableModel.createColumnsFromBiqlQuery<TRow>(biqlQuery);
    super(columns, []);

    this.biqlQuery = biqlQuery;
    this.realtimeUpdateCallback = onRealtimeUpdate;

    // Convert old row actions to new format
    if (oldRowActions) {
      this.rowActions = BiqlTableModel.convertRowActions(oldRowActions);
    }

    // Execute the query and populate data
    this.executeQuery();
  }

  private static createColumnsFromBiqlQuery<TRow extends Record<string, unknown>>(
    biqlQuery: BiqlQuery,
  ): ColumnDef<TRow>[] {
    const columns: ColumnDef<TRow>[] = [];

    // Convert regular fields to columns
    biqlQuery.fields().forEach((field: BiqlField) => {
      const fieldName = field.name;
      if (!fieldName) return;

      // Skip hidden fields
      if (field.hidden) return;

      const column: ColumnDef<TRow> = {
        id: fieldName,
        header: field.label || fieldName,
        width: 150,
        accessor: (row: TRow) => row[fieldName],
        // Try to infer data type
        dataType: BiqlTableModel.inferDataType(fieldName),
        sortable: true,
        visible: !field.hidden,
      };

      columns.push(column);
    });

    // Convert calculated fields to columns
    biqlQuery.calculatedFields().forEach((calcField: CalculatedField) => {
      const fieldName = calcField.name;
      if (!fieldName) return;

      const column: ColumnDef<TRow> = {
        id: fieldName,
        header: calcField.label || fieldName,
        width: 150,
        accessor: (row: TRow) =>
          calcField.getValue
            ? calcField.getValue(row, calcField)
            : row[fieldName],
        sortable: true,
        visible: true,
      };

      // If there's a custom renderer, use it
      if (calcField.getDisplayComponent) {
        column.cellRenderer = (
          value: unknown,
          row: TRow,
          _rowIndex: number,
        ): ReactNode => {
          return calcField.getDisplayComponent(row, calcField);
        };
      }

      columns.push(column);
    });

    return columns;
  }

  private static inferDataType(
    fieldName: string,
  ): StringDataType | NumberDataType | undefined {
    // Simple heuristics to infer data type from field name
    const lowerName = fieldName.toLowerCase();

    if (
      lowerName.includes("count") ||
      lowerName.includes("size") ||
      lowerName.includes("amount") ||
      lowerName.includes("total") ||
      lowerName.includes("num") ||
      lowerName === "id" ||
      lowerName === "uid"
    ) {
      return new NumberDataType();
    }

    return new StringDataType();
  }

  private async executeQuery(): Promise<void> {
    try {
      const response = await QubesAPI.get().biqlQuery<TRow>(
        this.biqlQuery.buildQueryString(),
        this.biqlQuery.appSpace(),
        "verbose",
      );

      // Update the data
      this.data = response.data;

      // Notify callback if provided
      if (this.realtimeUpdateCallback) {
        this.realtimeUpdateCallback(this.data);
      }

      console.log(`BiqlTableModel: Loaded ${response.data.length} rows`);
    } catch (error) {
      console.error("Error executing BiQL query:", error);
    }
  }

  /**
   * Refresh the data by re-executing the query
   */
  async refresh(): Promise<void> {
    await this.executeQuery();
  }

  /**
   * Get the underlying BiqlQuery
   */
  getBiqlQuery(): BiqlQuery {
    return this.biqlQuery;
  }

  /**
   * Convert old RowAction format to new RowAction format
   */
  private static convertRowActions<TRow>(
    oldActions: OldRowAction<TRow>[],
  ): RowAction<TRow>[] {
    return oldActions.map((oldAction) => {
      return {
        name: oldAction.name,
        icon: oldAction.icon, // Keep icon for now, we can map to emoji later
        handler: async (row: TRow, rowIndex: number) => {
          // The old action expects an ActionCtx, but we don't have all that context
          // in the new table. We'll create a minimal context.
          // Note: This is a simplified adapter - some features may not work
          if (oldAction.run) {
            await oldAction.run({
              // Legacy-adapter shim: the old ActionCtx requires a tableModel + MUI
              // api this new-table path does not have, so it knowingly passes
              // undefined ("some features may not work", above).
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              tableModel: undefined as any,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              api: undefined as any,
              row,
              showModalFn: (json: object) => {
                // Fallback behavior for legacy actions that expect a modal.
                console.log("Modal data:", json);
                notify({
                  severity: "info",
                  title: "Details available",
                  message: "Action details were logged to the browser console.",
                });
              },
              rowIndex,
              colIndex: undefined,
            });
          }
        },
      };
    });
  }
}

/**
 * Factory function to create a BiqlTableModel
 */
export function createBiqlTableModel<TRow extends Record<string, unknown> = Record<string, unknown>>(
  biqlQuery: BiqlQuery,
  oldRowActions?: OldRowAction<TRow>[],
  onRealtimeUpdate?: (rows: TRow[]) => void,
): BiqlTableModel<TRow> {
  return new BiqlTableModel<TRow>(biqlQuery, oldRowActions, onRealtimeUpdate);
}
